import React, { useState, useEffect } from 'react';
import TwDialog from '../TwDialog/TwDialog';
import { TwButton, TwButtonType } from '../TwButtons/TwButtons';
import LoadNotificationOptions from '../../pages/Settings/AppSettings/LoadNotificationSettings/LoadNotificationOptions/LoadNotificationOptions';
import {
  LoadNotificationRecipients,
  LoadNotificationStatusEvents,
  LoadNotificationStopStatusEvents,
  LoadNotificationStopTypes
} from '../../providers/Settings/LoadNotificationSettings.model';
import TwTagsField, { TwTagsOption } from '../TwTagsField/TwTagsField';
import { ClientResponse } from '../../redux/api/customer/customer.model';
import { getCustomerContactEmails } from '../../utils/contactUtils';
import useAuth from '../../providers/Auth/Auth.provider';

export interface TwLoadNotificationValues {
  id?: string;
  order_id?: string | null;
  recipients: LoadNotificationRecipients;
  order_status_events: LoadNotificationStatusEvents;
  stop_status_events: LoadNotificationStopStatusEvents;
  stop_types: LoadNotificationStopTypes;
}

interface TwLoadNotificationOptionsProps {
  open: boolean;
  loadId: string;
  onClose: () => void;
  isEditLoad: boolean;
  onSavedConfig: (config: TwLoadNotificationValues) => void;
  loadCustomer: ClientResponse;
  savedSettings?: TwLoadNotificationValues;
}

const DEFAULT_LOAD_NOTIFICATION_VALUES: TwLoadNotificationValues = {
  id: '',
  order_id: null,
  recipients: {
    customer_contact_email: false,
    customer_billing_email: false,
    user_dispatching_order: false,
    dispatcher_assigned_on_order: false,
    additional_emails: []
  },
  order_status_events: {
    created: false,
    in_transit: false,
    delivered: false
  },
  stop_status_events: {
    in_transit: false,
    arrived: false,
    complete: false
  },
  stop_types: {
    pickup: false,
    drop_off: false,
    transfer: false,
    none: false
  }
};

const TwEditLoadNotificationSettings: React.FC<TwLoadNotificationOptionsProps> = ({
  open,
  loadId,
  loadCustomer,
  isEditLoad,
  onClose,
  onSavedConfig,
  savedSettings
}) => {
  const {
    data: { userData },
    getTenantSetting
  } = useAuth();

  const [selectedSettings, setSelectedSettings] = useState<TwLoadNotificationValues>(
    DEFAULT_LOAD_NOTIFICATION_VALUES
  );

  const [sendToEmails, setSendToEmails] = useState<string[]>([]);
  const [suggestedEmails, setSuggestedEmails] = useState<TwTagsOption[]>([]);

  const includeNoticeOfAssignmentEnabled = getTenantSetting(
    'enable-factoring-notice-of-assignment'
  );

  const handleClose = (): void => {
    setSendToEmails([]);
    setSuggestedEmails([]);
    setSelectedSettings(DEFAULT_LOAD_NOTIFICATION_VALUES);
    onClose();
  };

  const handleSaveConfig = (): void => {
    /** To edit specific load notification settings, we don't save the general config ID
    because it's not needed in the backend **/
    const settingsToSave: TwLoadNotificationValues = {
      order_id: isEditLoad ? loadId : null,
      recipients: {
        ...selectedSettings.recipients,
        additional_emails: sendToEmails
      },
      order_status_events: selectedSettings.order_status_events,
      stop_status_events: selectedSettings.stop_status_events,
      stop_types: selectedSettings.stop_types
    };

    if (isEditLoad && selectedSettings.id) {
      settingsToSave.id = selectedSettings.id;
    }
    onSavedConfig(settingsToSave);
    handleClose();
  };

  useEffect(() => {
    loadCustomer &&
      setSuggestedEmails(
        getCustomerContactEmails(
          loadCustomer,
          includeNoticeOfAssignmentEnabled,
          userData?.factoringCompany
        )
      );
  }, [loadCustomer, includeNoticeOfAssignmentEnabled, userData]);

  useEffect(() => {
    if (!open && !savedSettings) {
      return;
    }

    savedSettings && setSendToEmails(savedSettings?.recipients.additional_emails);
  }, [open, savedSettings]);

  return (
    <TwDialog
      onClickClose={handleClose}
      open={open}
      title="Edit order notification settings"
      actions={[
        <TwButton key="1" type={TwButtonType.secondary} label="Cancel" action={handleClose} />,
        <TwButton key="2" type={TwButtonType.primary} label="Save" action={handleSaveConfig} />
      ]}
    >
      <LoadNotificationOptions
        loadId={loadId}
        temporarySettings={savedSettings}
        handleSelectedSettings={(selected): void => {
          setSelectedSettings(selected);
        }}
        isSettingsPage={false}
      />
      <TwTagsField
        id="load-notification-emails"
        label="Additional recipients:"
        tagsDisplayed={sendToEmails}
        tagType={'email'}
        onTagAdded={(emails): void => {
          setSendToEmails(emails);
        }}
        suggestedOptions={suggestedEmails}
        placeholder="Optional"
      />
    </TwDialog>
  );
};

export default TwEditLoadNotificationSettings;
